import React, { useState } from 'react';
import { X, Shield, FileText, Trash2, CheckCircle2, Lock, MapPin, Server } from 'lucide-react';
import { Language } from '../types/spot';

interface PrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: Language;
  initialTab?: 'privacy' | 'imprint';
}

export const PrivacyModal: React.FC<PrivacyModalProps> = ({
  isOpen,
  onClose,
  language,
  initialTab = 'privacy',
}) => {
  const [activeTab, setActiveTab] = useState<'privacy' | 'imprint'>(initialTab);

  if (!isOpen) return null;

  const t = {
    de: {
      tabPrivacy: 'Datenschutz',
      tabImprint: 'Impressum',
      confirmReset: 'Möchtest du alle lokal gespeicherten Favoriten, besuchten Orte und Einstellungen aus deinem Browser löschen?',
      title: 'Rechtliche Hinweise & Datenschutz',
      subtitle: 'DSGVO-konforme Datensparsamkeit · Lokale Verarbeitung',
      close: 'Schließen',
      imprintTitle: 'Impressum / Angaben gemäß § 5 TMG',
      imprintIntro: 'Hinweis für den Plattform-Betreiber: Bitte tragen Sie hier vor dem kommerziellen Rollout Ihre individuellen Kontaktdaten ein.',
      imprintOperatorTitle: 'Diensteanbieter:',
      imprintOperatorText: '[Firma / Betreiber eintragen]\n[Straße und Hausnummer]\n[PLZ Ort, Land]',
      imprintContactTitle: 'Kontakt:',
      imprintContactText: 'E-Mail: [kontakt@ihre-domain.com]\nTelefon: [+49 (0) ...]\nWeb: [https://ihre-domain.com]',
      imprintRegisterTitle: 'Registereintrag (falls juristische Person):',
      imprintRegisterText: 'Registergericht: [Amtsgericht ...]\nRegisternummer: [HRB ...]\nUmsatzsteuer-ID: [DE... gemäß § 27a UStG]',
      imprintContentTitle: 'Verantwortlich für redaktionelle Inhalte (§ 18 Abs. 2 MStV):',
      imprintContentText: '[Name der verantwortlichen Person]\n[Anschrift wie oben]',
      imprintDisclaimerTitle: 'Haftung für Links & Kartendienste:',
      imprintDisclaimerText: 'Unser Angebot enthält Links zu externen Webseiten Dritter (u. a. Naver Corp., Kakao Corp., Google LLC), auf deren Inhalte wir keinen Einfluss haben. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.',
      sec1Title: '1. Standortbezogene Daten (HTML5 Geolocation API)',
      sec1Text: 'Die App bietet standortbasierte Funktionen (Distanzberechnung, Richtungsanzeige und Sortierung nach Gehweite). Sofern Sie der Standortfreigabe in Ihrem Browser zustimmen (Art. 6 Abs. 1 lit. a DSGVO), werden Ihre GPS-Koordinaten ausschließlich lokal im Arbeitsspeicher Ihres Browsers verarbeitet. Es findet keine serverseitige Übertragung oder Speicherung Ihres Standorts statt.',
      sec2Title: '2. Webhosting & Content Delivery Network (Cloudflare Pages)',
      sec2Text: 'Diese Webanwendung wird über Cloudflare Pages (Cloudflare Inc., USA) bereitgestellt. Beim Aufruf erfasst Cloudflare standardmäßige technische Zugriffsdaten (Server-Logfiles zur Ausfallsicherheit und DDoS-Abwehr) gemäß Art. 6 Abs. 1 lit. f DSGVO.',
      sec3Title: '3. Lokale Datenspeicherung (Local Storage)',
      sec3Text: 'Gemerkte Favoriten, als besucht markierte Orte und Ihre Spracheinstellung werden ausschließlich im lokalen Speicher Ihres Endgeräts (window.localStorage) gespeichert. Es werden keine Profilbildungs-Cookies oder Cross-Site-Tracker verwendet.',
      sec4Title: '4. Externe Kartenschnittstellen (KakaoMap, Naver Map)',
      sec4Text: 'Für Navigationsverknüpfungen stellt die Plattform Direktlinks zu den offiziellen Kartenplattformen KakaoMap und Naver Map bereit. Beim Klick auf diese Links verlassen Sie unsere Website; es gelten die Datenschutzbestimmungen des jeweiligen Kartenanbieters.',
      sec5Title: '5. Rechte der betroffenen Person & Datenlöschung',
      sec5Text: 'Da wir keine personenbezogenen Daten auf unseren Servern speichern, verbleiben alle Daten vollständig unter Ihrer Kontrolle. Sie können alle im Browser gespeicherten Daten jederzeit selbst löschen.',
      clearTitle: 'Lokale Daten bereinigen',
      clearSub: 'Löscht alle gespeicherten Favoriten, besuchten Orte und Einstellungen auf diesem Gerät.',
      clearBtn: 'Alle lokalen Daten löschen',
      compliance: 'DSGVO / GDPR Konformität',
      understoodBtn: 'Schließen',
    },
    en: {
      tabPrivacy: 'Privacy Policy',
      tabImprint: 'Legal Notice / Imprint',
      confirmReset: 'Do you want to clear all locally saved favorites and settings from your browser?',
      title: 'Legal & Privacy Information',
      subtitle: 'GDPR Compliant · 100% Client-Side Local Data Processing',
      close: 'Close',
      imprintTitle: 'Legal Notice / Provider Identification',
      imprintIntro: 'Operator Note: Please insert your registered commercial contact details below prior to commercial launch.',
      imprintOperatorTitle: 'Service Provider:',
      imprintOperatorText: '[Company / Operator Name]\n[Street & Number]\n[Postal Code, City, Country]',
      imprintContactTitle: 'Contact:',
      imprintContactText: 'Email: [contact@your-domain.com]\nPhone: [+49 (0) ...]\nWeb: [https://your-domain.com]',
      imprintRegisterTitle: 'Commercial Register (if incorporated):',
      imprintRegisterText: 'Court of Registration: [Court ...]\nRegistration Number: [HRB ...]\nVAT ID: [VAT ID if applicable]',
      imprintContentTitle: 'Content Responsibility:',
      imprintContentText: '[Name of Responsible Editor]\n[Address as above]',
      imprintDisclaimerTitle: 'Disclaimer for External Links & Maps:',
      imprintDisclaimerText: 'Our platform contains links to external services (Naver Map, KakaoMap, Google Maps). We do not control and cannot assume liability for third-party content and map representations.',
      sec1Title: '1. Location Data (HTML5 Geolocation API)',
      sec1Text: 'Optional location features process your GPS coordinates strictly inside your browser memory (Art. 6(1)(a) GDPR). Coordinates are never transferred or stored on external application servers.',
      sec2Title: '2. Web Hosting & CDN (Cloudflare Pages)',
      sec2Text: 'This application is hosted statically via Cloudflare Pages (Cloudflare Inc., USA). Technical server access logs are handled for cybersecurity and DDoS resilience pursuant to Art. 6(1)(f) GDPR.',
      sec3Title: '3. Local Client Storage',
      sec3Text: 'Saved favorites, visited spots, and language choices remain strictly in your browser (window.localStorage). We do not deploy advertising cookies or cross-site tracking pixels.',
      sec4Title: '4. External Map Integrations (KakaoMap, Naver Map)',
      sec4Text: 'Navigation buttons link directly to official KakaoMap and Naver Map endpoints. Opening these links invokes the privacy policies of the respective providers.',
      sec5Title: '5. Data Erasure & User Rights',
      sec5Text: 'Because no personal data is stored server-side, you retain complete authority over your device data and may purge it at any time.',
      clearTitle: 'Clear Local Storage Data',
      clearSub: 'Erases all saved bookmarks, visited spots, and preferences on this device.',
      clearBtn: 'Clear Local Data',
      compliance: 'GDPR Compliance',
      understoodBtn: 'Close',
    },
    kr: {
      tabPrivacy: '개인정보 처리방침',
      tabImprint: '서비스 사업자 정보',
      confirmReset: '브라우저에 저장된 모든 즐겨찾기 및 설정을 삭제하시겠습니까?',
      title: '법적 고지 및 개인정보 안내',
      subtitle: '개인정보 보호 원칙 준수 · 브라우저 로컬 처리',
      close: '닫기',
      imprintTitle: '서비스 제공자 정보',
      imprintIntro: '상업적 런칭 전 운영자 및 법인 정보를 등록할 수 있습니다.',
      imprintOperatorTitle: '운영 주체:',
      imprintOperatorText: '[운영자 / 법인명 입력]\n[사업장 주소]\n[우편번호 및 국가]',
      imprintContactTitle: '고객 문의:',
      imprintContactText: '이메일: [contact@your-domain.com]\n전화: [연락처]\n웹사이트: [도메인]',
      imprintRegisterTitle: '사업자 등록 정보:',
      imprintRegisterText: '사업자등록번호: [등록번호]\n통신판매업신고: [신고번호]',
      imprintContentTitle: '콘텐츠 책임:',
      imprintContentText: '[책임자 성명]\n[주소]',
      imprintDisclaimerTitle: '외부 링크 및 지도 안내:',
      imprintDisclaimerText: '본 플랫폼은 카카오맵, 네이버 지도 등 공식 지도 서비스로 연결되는 링크를 제공합니다. 외부 지도 서비스 이용 시 해당 업체의 약관이 적용됩니다.',
      sec1Title: '1. 위치 정보 처리 (HTML5 Geolocation API)',
      sec1Text: '위치 정보는 직선 거리 및 경로 추천을 위해 이용자 브라우저 내부에서만 일시적으로 처리되며 서버로 전송되거나 저장되지 않습니다.',
      sec2Title: '2. 웹 호스팅 및 보안 (Cloudflare Pages)',
      sec2Text: '안정적인 정적 웹 서비스 제공을 위해 Cloudflare Pages 인프라를 활용합니다.',
      sec3Title: '3. 로컬 스토리지 보관',
      sec3Text: '저장한 장소 및 방문 체크 내역은 사용자 기기(localStorage)에만 저장됩니다.',
      sec4Title: '4. 외부 지도 서비스 연동',
      sec4Text: '카카오맵 및 네이버 지도 링크를 통해 편리한 대중교통 경로 안내를 제공합니다.',
      sec5Title: '5. 데이터 삭제 권리',
      sec5Text: '브라우저 캐시 삭제 또는 아래의 버튼을 통해 저장된 데이터를 언제든 초기화할 수 있습니다.',
      clearTitle: '로컬 데이터 초기화',
      clearSub: '이 기기에 저장된 모든 북마크 및 설정을 삭제합니다.',
      clearBtn: '로컬 데이터 삭제',
      compliance: '개인정보 보호 원칙 준수',
      understoodBtn: '닫기',
    },
  }[language];

  const handleClearLocalStorage = () => {
    if (window.confirm(t.confirmReset)) {
      localStorage.clear();
      window.location.reload();
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 640,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          background: '#ffffff',
          borderRadius: 16,
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--border)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'var(--bg-elevated)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-primary)',
              }}
            >
              <Shield style={{ width: 18, height: 18 }} />
            </div>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0, color: 'var(--text-primary)' }}>
                {t.title}
              </h2>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                {t.subtitle}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="btn btn--icon-sm"
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: '#ffffff',
              border: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            aria-label={t.close}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Tab switcher: Datenschutz vs Impressum */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            padding: '0 20px',
            gap: 16,
          }}
        >
          <button
            onClick={() => setActiveTab('privacy')}
            style={{
              padding: '12px 4px',
              border: 'none',
              background: 'none',
              fontSize: 13.5,
              fontWeight: 700,
              cursor: 'pointer',
              color: activeTab === 'privacy' ? 'var(--ink)' : 'var(--text-muted)',
              borderBottom: activeTab === 'privacy' ? '2px solid var(--ink)' : '2px solid transparent',
              transition: 'all 150ms',
            }}
          >
            {t.tabPrivacy}
          </button>
          <button
            onClick={() => setActiveTab('imprint')}
            style={{
              padding: '12px 4px',
              border: 'none',
              background: 'none',
              fontSize: 13.5,
              fontWeight: 700,
              cursor: 'pointer',
              color: activeTab === 'imprint' ? 'var(--ink)' : 'var(--text-muted)',
              borderBottom: activeTab === 'imprint' ? '2px solid var(--ink)' : '2px solid transparent',
              transition: 'all 150ms',
            }}
          >
            {t.tabImprint}
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {activeTab === 'privacy' ? (
            <>
              {/* Privacy Sections */}
              <div>
                <h4 style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                  {t.sec1Title}
                </h4>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
                  {t.sec1Text}
                </p>
              </div>

              <div>
                <h4 style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                  {t.sec2Title}
                </h4>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
                  {t.sec2Text}
                </p>
              </div>

              <div>
                <h4 style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                  {t.sec3Title}
                </h4>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
                  {t.sec3Text}
                </p>
              </div>

              <div>
                <h4 style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                  {t.sec4Title}
                </h4>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
                  {t.sec4Text}
                </p>
              </div>

              <div>
                <h4 style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                  {t.sec5Title}
                </h4>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
                  {t.sec5Text}
                </p>
              </div>

              {/* Data Reset Box */}
              <div
                style={{
                  marginTop: 8,
                  padding: 14,
                  borderRadius: 10,
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                    {t.clearTitle}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                    {t.clearSub}
                  </div>
                </div>

                <button
                  onClick={handleClearLocalStorage}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    background: '#ffffff',
                    border: '1px solid var(--border)',
                    color: 'var(--error)',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Trash2 style={{ width: 14, height: 14 }} />
                  <span>{t.clearBtn}</span>
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Imprint Section */}
              <div
                style={{
                  padding: 12,
                  borderRadius: 8,
                  background: 'var(--bg-elevated)',
                  border: '1px dashed var(--border)',
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                }}
              >
                {t.imprintIntro}
              </div>

              <div>
                <h4 style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                  {t.imprintOperatorTitle}
                </h4>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-line', lineHeight: 1.5, margin: 0 }}>
                  {t.imprintOperatorText}
                </p>
              </div>

              <div>
                <h4 style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                  {t.imprintContactTitle}
                </h4>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-line', lineHeight: 1.5, margin: 0 }}>
                  {t.imprintContactText}
                </p>
              </div>

              <div>
                <h4 style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                  {t.imprintRegisterTitle}
                </h4>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-line', lineHeight: 1.5, margin: 0 }}>
                  {t.imprintRegisterText}
                </p>
              </div>

              <div>
                <h4 style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                  {t.imprintContentTitle}
                </h4>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-line', lineHeight: 1.5, margin: 0 }}>
                  {t.imprintContentText}
                </p>
              </div>

              <div>
                <h4 style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                  {t.imprintDisclaimerTitle}
                </h4>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
                  {t.imprintDisclaimerText}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--border)',
            background: 'var(--bg-elevated)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
            <CheckCircle2 style={{ width: 14, height: 14, color: 'var(--success)' }} />
            <span>{t.compliance}</span>
          </div>

          <button
            onClick={onClose}
            className="btn btn--primary btn--sm"
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              background: 'var(--ink)',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: 12.5,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {t.understoodBtn}
          </button>
        </div>
      </div>
    </div>
  );
};
